/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Flex,
  Grid,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
  Skeleton,
  SkeletonText,
  GridItem,
  ModalFooter,
} from '@chakra-ui/react';
import Button from '@/components/common/Button';
import Field from './Field';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAddOnDetail } from '@/services/manageAddons';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { useState, useMemo } from 'react';
import CustomImageSlider from '@/components/common/ImageSlider';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { formatApiDateToDisplay } from '@/utils/dateUtils';

interface SelectedImagesState {
  images: string[];
  index: number;
}

const AddOnDetail = () => {
  const { addonId } = useParams<{ addonId: string }>();
  const { data: addOn, isLoading } = useGetAddOnDetail(addonId ?? '');
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedImages, setSelectedImages] = useState<SelectedImagesState>({
    images: [],
    index: 0,
  });

  const handleImageClick = (images: string[], index: number) => {
    setSelectedImages({ images, index });
    onOpen();
  };

  // ────────────────────────────────────────────────
  //  Group variants by color + collect images from variants that actually have them
  // ────────────────────────────────────────────────
  const groupedVariants = useMemo(() => {
    if (!addOn?.product_variants?.length) return [];

    const groups: Record<
      string,
      {
        colorName: string;
        hexCode: string;
        images: string[];
        sizeDetails: any[];
      }
    > = {};

    addOn.product_variants.forEach((variant: any) => {
      const colorName = variant.color || 'Unknown';
      const hexCode = variant.value || '';
      const key = `${colorName}_${hexCode}`;

      if (!groups[key]) {
        groups[key] = {
          colorName,
          hexCode,
          images: [],
          sizeDetails: [],
        };
      }

      // Collect images only if they exist (skip empty arrays)
      if (variant.images?.length > 0) {
        const newImageUrls = variant.images
          .map(
            (img: { image_medium: string; image: string }) => img.image_medium || img.image || '',
          )
          .filter(Boolean);

        // Add only new / unique images
        newImageUrls.forEach((url: string) => {
          if (!groups[key].images.includes(url)) {
            groups[key].images.push(url);
          }
        });
      }

      // Add size info
      groups[key].sizeDetails.push({
        id: variant.id,
        size: variant.size?.value || variant.size?.size || '-',
        price: variant.variant_price,
        weight: variant.variant_weight,
        stock_quantity: variant.stock_quantity,
        sku: variant.sku,
        is_primary: variant.is_primary,
      });
    });

    return Object.values(groups);
  }, [addOn]);

  const renderSkeletonField = (label: string, width = '150px') => (
    <GridItem>
      <VStack align="start" gap={1}>
        <Text color="text.secondary" fontSize="xs">
          {label}
        </Text>
        <SkeletonText bg="button.disable" noOfLines={1} width={width} />
      </VStack>
    </GridItem>
  );

  const renderSkeletonCategory = () => (
    <VStack gap={4} alignItems="start" flex="1">
      <Text fontSize="md" color="text.primary" fontWeight="semibold">
        Category Details
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" gap={2} width="100%">
        {renderSkeletonField('Category')}
      </Grid>
    </VStack>
  );

  const renderSkeletonGeneral = () => (
    <VStack gap={4} alignItems="start" flex="1">
      <Text fontSize="md" color="text.primary" fontWeight="semibold">
        Add-on Details
      </Text>
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(5, 1fr)' }} gap={4} width="100%">
        {renderSkeletonField('Name', '100%')}
        {renderSkeletonField('Discount')}
        {renderSkeletonField('Description', '100%')}
        {renderSkeletonField('Created At')}
        {renderSkeletonField('Updated At')}
      </Grid>
    </VStack>
  );

  const renderSkeletonVariant = () => (
    <VStack
      gap={4}
      alignItems="start"
      width="100%"
      p={4}
      border="1px"
      borderColor="border.default"
      bg="background.white"
      borderRadius="lg"
    >
      <HStack align="center" gap={3}>
        <Skeleton height="30px" width="30px" borderRadius="md" />
        <SkeletonText noOfLines={1} width="120px" />
      </HStack>
      <VStack alignItems="start" width="100%">
        <Text color="text.secondary" fontSize="xs">
          Photos
        </Text>
        <Flex gap={4} wrap="wrap">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height="200px" width="200px" borderRadius="lg" />
          ))}
        </Flex>
      </VStack>
    </VStack>
  );

  return (
    <Flex direction="column" gap={5} m={4} fontFamily="primary">
      {/* Category Section */}
      <HStack
        p={4}
        border="1px solid"
        bg="#FFFFFF"
        borderRadius="lg"
        borderColor="border.default"
        justifyContent="space-between"
        alignItems="start"
        width="100%"
      >
        {isLoading ? (
          renderSkeletonCategory()
        ) : (
          <VStack gap={4} alignItems="start" flex="1">
            <Text fontSize="md" color="text.primary" fontWeight="semibold">
              Category Details
            </Text>
            <Grid templateColumns="repeat(2, 1fr)" gap={2} width="100%">
              <Field
                label="Category"
                value={addOn?.sub_category?.name ?? addOn?.category?.name ?? '-'}
              />
            </Grid>
          </VStack>
        )}

        <Button
          ml="auto"
          fontSize="md"
          borderColor="brand.primary.DEFAULT"
          fontWeight="semibold"
          bg="#fff"
          color="brand.primary.DEFAULT"
          border="2px"
          _hover={{
            color: '#fff',
            bg: 'brand.primary.DEFAULT',
          }}
          onClick={() => {
            if (addonId) {
              navigate(NAVIGATION_ROUTES.EDIT_ADDONS.replace(':addonId', addonId));
            }
          }}
          isDisabled={isLoading}
        >
          Edit Add-on
        </Button>
      </HStack>

      {/* General Info Section */}
      <HStack
        p={4}
        border="1px solid"
        borderRadius="lg"
        bg="#FFFFFF"
        borderColor="border.default"
        justifyContent="space-between"
        alignItems="start"
        width="100%"
      >
        {isLoading ? (
          renderSkeletonGeneral()
        ) : (
          <VStack gap={4} alignItems="start" flex="1">
            <Text fontSize="md" color="text.primary" fontWeight="semibold">
              Add-on Details
            </Text>
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }}
              gap={4}
              width="100%"
            >
              <Field label="Name" value={addOn?.name ?? '-'} />
              <Field
                label="Discount"
                value={
                  addOn?.discount_percent
                    ? `${parseFloat(addOn.discount_percent).toFixed(2)}%`
                    : '-'
                }
              />
              <Field label="Description" value={addOn?.description ?? '-'} />
              <Field
                label="Created At"
                value={formatApiDateToDisplay(addOn?.created_at ?? '') ?? '-'}
              />
              <Field
                label="Updated At"
                value={formatApiDateToDisplay(addOn?.updated_at ?? '') ?? '-'}
              />
              <Field label="Status" value={addOn?.is_active ? 'Active' : 'Inactive'} />
            </Grid>
          </VStack>
        )}
      </HStack>

      {/* Variant Details Section */}
      <HStack
        p={4}
        border="1px solid"
        borderRadius="lg"
        borderColor="border.default"
        bg="#FFFFFF"
        width="100%"
      >
        <VStack gap={6} alignItems="start" width="100%">
          <Text fontSize="md" color="text.primary" fontWeight="semibold">
            Variant Details
          </Text>

          {isLoading ? (
            <>
              {Array(2)
                .fill(0)
                .map((_, i) => (
                  <Box key={i}>{renderSkeletonVariant()}</Box>
                ))}
            </>
          ) : groupedVariants.length > 0 ? (
            groupedVariants.map((group, groupIndex) => (
              <VStack
                key={groupIndex}
                gap={4}
                alignItems="start"
                width="100%"
                p={4}
                border="1px"
                borderColor="border.default"
                bg="background.white"
                borderRadius="lg"
              >
                <HStack align="center" gap={3}>
                  <Box
                    w="30px"
                    h="30px"
                    bg={group.hexCode || 'gray.300'}
                    borderRadius="md"
                    border="1px solid gray.300"
                  />
                  <Text fontSize="md" color="text.primary" fontWeight="semibold">
                    {group.colorName || 'No color'}
                  </Text>
                </HStack>

                {/* Images - only from variants that actually have images */}
                <VStack alignItems="start" width="100%">
                  <Text color="text.secondary" fontSize="xs">
                    Photos
                  </Text>
                  <Flex gap={4} direction={{ base: 'column', lg: 'row' }} wrap="wrap">
                    {group.images.length > 0 ? (
                      group.images.map((imgUrl, imgIdx) => (
                        <Image
                          key={imgUrl}
                          src={imgUrl}
                          h="200px"
                          w="200px"
                          objectFit="cover"
                          objectPosition="top"
                          borderRadius="lg"
                          cursor="pointer"
                          onClick={() => handleImageClick(group.images, imgIdx)}
                          fallback={<Box bg="gray.100" w="200px" h="200px" borderRadius="lg" />}
                        />
                      ))
                    ) : (
                      <Text color="text.secondary" fontSize="sm">
                        No images available for this color
                      </Text>
                    )}
                  </Flex>
                </VStack>

                {/* Size Details */}
                <VStack alignItems="start" spacing={3} width="100%">
                  <Text color="text.secondary" fontSize="xs" fontWeight="medium">
                    Size Details
                  </Text>

                  {group.sizeDetails.map((sizeData: any, idx: number) => (
                    <Grid
                      key={idx}
                      templateColumns={{ base: '1fr', md: 'repeat(5, 1fr)' }}
                      gap={4}
                      width="100%"
                      p={3}
                      border="1px"
                      borderColor="border.default60"
                      borderRadius="md"
                      bg="background.secondary"
                    >
                      <Field label="Size" value={sizeData.size ?? '-'} />
                      <Field
                        label="Price"
                        value={`${getCurrencySymbol(addOn?.currency || 'NPR')}${sizeData.price ?? '-'}`}
                      />
                      <Field label="Weight (kg)" value={sizeData.weight ?? '-'} />
                      <Field label="Stock" value={sizeData.stock_quantity ?? '0'} />
                      <Field label="SKU" value={sizeData.sku ?? '-'} />
                      <Field label="Primary" value={sizeData.is_primary ? 'Yes' : 'No'} />
                    </Grid>
                  ))}
                </VStack>
              </VStack>
            ))
          ) : (
            <Text color="text.secondary" fontSize="sm">
              No variants available
            </Text>
          )}
        </VStack>
      </HStack>

      {/* Image Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Images</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CustomImageSlider images={selectedImages.images} initialIndex={selectedImages.index} />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default AddOnDetail;
